import { createSlice, current } from "@reduxjs/toolkit";
import { getStatusMessage } from "../../utils/response-code-messages";
import { get, isEqual } from "lodash";
import Snackbar from "../../common/snackBar";
import {
  FactoryDataRequestActionTypes,
  SearchtActionTypes,
} from "../models/factory-management";
import * as uuid from "uuid";

const page_name = "factoryManagement";

const myUniqueId = uuid.v4().replace(/-/g, "").slice(0, 8);

interface COPY_ASSET_LIST_TYPE {
  asset_id: string;
  asset_name: string;
  asset_ip: string;
}

interface FACTORY_STATE_TYPES {
  isFactoriesListLoading: boolean;
  factoriesList: Array<[]>;
  factoriesListTotalRowCount: number;
  factoriesListPageNumber: number;
  factoriesListError: boolean;
  isAddFactoryLoading: boolean;
  isAddFactorySuccess: boolean;
  isAddFactoryError: boolean;
  isupdateFactoryLoading: boolean;
  isUpdateFactorySuccess: boolean;
  isupdateFactoryError: boolean;
  isRemoveFactoryLoading: boolean;
  isRemoveFactorySuccess: boolean;
  isRemoveFactoryError: boolean;
  isFactoryLoading: boolean;
  selectedFactory: object;
  isGetFactoryDataError: boolean;
  hello: string;
  isUpdateFactoryStatusSuccess: boolean;
  isupdateFactoryStatusError: boolean;
  approver: boolean;
  searchItem: undefined | string;
  factoryPageName: string;

  assetComponentsLoading: boolean;
  assetComponentsRequestFailed: boolean;
  assetComponentsData: any;
  activeAssetId: string;

  myUniqueId: string;
  scanTagsLoading: boolean;
  scanTagsRequestFailed: boolean;
  scanTagsData?: any;
  scanTagsFailedIds?: any[];
  uiConfigNodes: any[];

  copyConfigurationFromList: COPY_ASSET_LIST_TYPE[];
  loadingCopyConfigurationFromList: boolean;
  errorCopyConfigurationFromList: boolean;

  copyConfigurationFromComponent: any;
  loadingCopyConfigurationFromComponent: boolean;
  errorCopyConfigurationFromComponent: boolean;
}

const initialState: FACTORY_STATE_TYPES = {
  isFactoriesListLoading: false,
  factoriesList: [],
  factoriesListTotalRowCount: 0,
  factoriesListPageNumber: 1,
  factoriesListError: false,
  isAddFactoryLoading: false,
  isAddFactorySuccess: false,
  isAddFactoryError: false,
  isupdateFactoryLoading: false,
  isUpdateFactorySuccess: false,
  isupdateFactoryError: false,
  isRemoveFactoryLoading: false,
  isRemoveFactorySuccess: false,
  isRemoveFactoryError: false,
  isFactoryLoading: false,
  selectedFactory: {},
  isGetFactoryDataError: false,
  hello: "",
  isUpdateFactoryStatusSuccess: false,
  isupdateFactoryStatusError: false,
  approver: false,
  searchItem: "",
  factoryPageName: "Factory",

  assetComponentsLoading: false,
  assetComponentsRequestFailed: false,
  assetComponentsData: {},
  activeAssetId: "",
  myUniqueId,
  scanTagsLoading: false,
  scanTagsRequestFailed: false,
  scanTagsData: {},
  scanTagsFailedIds: [],
  uiConfigNodes: [],

  copyConfigurationFromList: [],
  loadingCopyConfigurationFromList: false,
  errorCopyConfigurationFromList: false,
  copyConfigurationFromComponent: {},
  loadingCopyConfigurationFromComponent: false,
  errorCopyConfigurationFromComponent: false,
};

const factoryManagementReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getFactoriesList: (state, action: FactoryDataRequestActionTypes) => {
      state.isFactoriesListLoading = true;
      state.factoriesListError = false;
      state.isAddFactorySuccess = false;
      state.isAddFactoryError = false;
      state.isUpdateFactorySuccess = false;
      state.isupdateFactoryError = false;
      state.isRemoveFactorySuccess = false;
      state.isRemoveFactoryError = false;
      state.isUpdateFactoryStatusSuccess = false;
      state.isupdateFactoryStatusError = false;
      state.factoryPageName = get(action, "payload.pageName", "Factory");
    },
    getFactoriesListResponse: (state, action) => {
      state.isFactoriesListLoading = false;
      if (state.searchItem === get(action, "payload.search_item", "")) {
        state.approver = get(action, "payload.approver", false);
        state.factoriesList = get(action, "payload.data", []);
        state.factoriesListTotalRowCount = get(
          action,
          "payload.total_count",
          0,
        );
        state.factoriesListPageNumber = get(action, "payload.page", 1);
      }
    },
    getFactoriesListFailed: (state, action) => {
      state.isFactoriesListLoading = false;
      state.factoriesListError = true;
      state.factoriesList = [];
      state.factoriesListTotalRowCount = 0;
      state.factoriesListPageNumber = 1;
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    getFactoryData: (state, action) => {
      state.isFactoryLoading = true;
      state.isGetFactoryDataError = false;
      // state.selectedFactory = {};
      state.factoryPageName = get(action, "payload.pageName", "Factory");
    },
    getFactoryDataResponse: (state, action) => {
      state.isFactoryLoading = false;
      state.selectedFactory = {
        ...get(action, "payload", {}),
        ui_config: {
          ...get(action, "payload.ui_config", {}),
          nodes: get(action, "payload.ui_config.nodes", []).map(
            (node: any) => ({
              ...node,
              position: {
                x: 0,
                y: 0,
              },
            }),
          ),
        },
      };
      state.uiConfigNodes = get(action, "payload.ui_config.nodes", []);
    },
    getFactoryDataFailed: (state, action) => {
      state.isFactoryLoading = false;
      state.isGetFactoryDataError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    addFactoryRequest: (state) => {
      state.isFactoriesListLoading = true;
      state.isAddFactoryLoading = true;
      state.isAddFactorySuccess = false;
      state.isAddFactoryError = false;
    },
    addFactoryResponse: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isAddFactoryLoading = false;
      state.isAddFactorySuccess = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace("Factory", state.factoryPageName));
    },
    addFactoryFailed: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isAddFactoryLoading = false;
      state.isAddFactoryError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    updateFactoryRequest: (state, action) => {
      state.isFactoriesListLoading = true;
      state.isupdateFactoryLoading = true;
      state.isUpdateFactorySuccess = false;
      state.isupdateFactoryError = false;
      state.factoryPageName = action.payload.pageName
        ? get(action, "payload.pageName", "Factory")
        : state.factoryPageName;
    },
    updateFactoryResponse: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isupdateFactoryLoading = false;
      state.isUpdateFactorySuccess = true;
      state.assetComponentsData = {};

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace("Factory", state.factoryPageName));
    },
    updateFactoryFailed: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isupdateFactoryLoading = false;
      state.isupdateFactoryError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    updateFactoryStatusRequest: (state) => {
      state.isFactoriesListLoading = true;
      state.isupdateFactoryLoading = true;
      state.isUpdateFactoryStatusSuccess = false;
      state.isupdateFactoryStatusError = false;
    },
    updateFactoryStatusResponse: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isupdateFactoryLoading = false;
      state.isUpdateFactoryStatusSuccess = true;
      state.isupdateFactoryStatusError = false;
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace("Factory", state.factoryPageName));
    },
    updateFactoryStatusFailed: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isupdateFactoryLoading = false;
      state.isupdateFactoryStatusError = true;
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    removeFactoryRequest: (state) => {
      state.isFactoriesListLoading = true;
      state.isRemoveFactoryLoading = true;
      state.isRemoveFactorySuccess = false;
      state.isRemoveFactoryError = false;
    },
    removeFactoryResponse: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isRemoveFactoryLoading = false;
      state.isRemoveFactorySuccess = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace("Factory", state.factoryPageName));
    },
    removeFactoryFailed: (state, action) => {
      state.isFactoriesListLoading = false;
      state.isRemoveFactoryLoading = false;
      state.isRemoveFactoryError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace("Factory", state.factoryPageName));
    },
    getAssetComponentsDataRequest: (state, action) => {
      state.assetComponentsLoading = true;
      state.assetComponentsRequestFailed = false;
    },
    getAssetComponentsDataResponse: (state, action) => {
      state.assetComponentsLoading = false;
      state.activeAssetId = action.payload.asset_id;
      const options = action.payload.options.map((opt: any, index: number) => ({
        ...opt,
        uuid:
          opt.uuid ??
          `asset:${myUniqueId}:${action.payload.asset_id}:${opt.component_id ?? opt.id ?? index}`,
      }));

      if (
        !isEqual(
          get(
            current(state),
            `assetComponentsData[${action.payload.asset_id}].components`,
            {},
          ),
          options,
        )
      ) {
        state.assetComponentsData = {
          ...state.assetComponentsData,
          [action.payload.asset_id]: {
            asset_id: action.payload.asset_id,
            components: options,
          },
        };
      }
      // if (state.assetComponentsData[action.payload.asset_id]) {
      //   if (!isEqual(state.assetComponentsData[action.payload.asset_id].components, action.payload.options)) {
      //     state.assetComponentsData[action.payload.asset_id].components = action.payload.options.map((opt: any) => ({
      //       ...opt,
      //       uuid: `asset:${myUniqueId}:${action.payload.asset_id}:${opt.component_id}`,
      //     }));
      //   }
      // } else {
      //   state.assetComponentsData = {
      //     ...state.assetComponentsData,
      //     [action.payload.asset_id]: {
      //       asset_id: action.payload.asset_id,
      //       components: action.payload.options.map((opt: any) => ({
      //         ...opt,
      //         uuid: `asset:${myUniqueId}:${action.payload.asset_id}:${opt.component_id}`,
      //       })),
      //     },
      //   };
      // }
      state.uiConfigNodes = state.uiConfigNodes.map((node: any) => {
        if (node.id === action.payload.asset_id) {
          return {
            ...node,
            data: {
              ...node.data,
              options:
                state.assetComponentsData[action.payload.asset_id].components,
            },
          };
        }
        return node;
      });
    },
    getAssetComponentsDataFailed: (state, action) => {
      state.assetComponentsLoading = false;
      state.assetComponentsRequestFailed = true;
    },
    getAssetComponentsDataBatchRequest: (state, action) => {
      state.assetComponentsLoading = true;
      state.assetComponentsRequestFailed = false;
    },
    getAssetComponentsDataBatchResponse: (state, action) => {
      state.assetComponentsLoading = false;
      // action.payload contains { data: { assetId: [...components], ... } }
      const batchData = action.payload.data || {};

      Object.entries(batchData).forEach(
        ([assetId, components]: [string, any]) => {
          const options = (components as any[]).map(
            (opt: any, index: number) => ({
              ...opt,
              uuid:
                opt.uuid ??
                `asset:${myUniqueId}:${assetId}:${opt.component_id ?? opt.id ?? index}`,
            }),
          );

          if (
            !isEqual(
              get(
                current(state),
                `assetComponentsData[${assetId}].components`,
                {},
              ),
              options,
            )
          ) {
            state.assetComponentsData = {
              ...state.assetComponentsData,
              [assetId]: {
                asset_id: assetId,
                components: options,
              },
            };
          }
        },
      );

      // Update uiConfigNodes for all affected assets
      state.uiConfigNodes = state.uiConfigNodes.map((node: any) => {
        if (state.assetComponentsData[node.id]) {
          return {
            ...node,
            data: {
              ...node.data,
              options: state.assetComponentsData[node.id].components,
            },
          };
        }
        return node;
      });
    },
    getAssetComponentsDataBatchFailed: (state, action) => {
      state.assetComponentsLoading = false;
      state.assetComponentsRequestFailed = true;
    },
    scanTagsRequest: (state, action) => {
      state.scanTagsLoading = true;
      state.scanTagsRequestFailed = false;
      // state.scanTagsFailedIds = state.scanTagsFailedIds?.filter((id) => action.payload.message.command_id !== id);
      state.scanTagsFailedIds = [];
    },
    scanTagsResponse: (state, action) => {
      state.scanTagsLoading = false;
      state.scanTagsRequestFailed = false;
    },
    scanTagsFailed: (state, action) => {
      state.scanTagsLoading = false;
      state.scanTagsRequestFailed = true;
      state.scanTagsFailedIds = [
        ...(state.scanTagsFailedIds || []),
        action.payload.data,
      ];

      Snackbar.error("Failed to scan tags. Please try again.");
    },
    getCopyConfigurationFromListRequest: (state, action) => {
      state.loadingCopyConfigurationFromList = true;
      state.errorCopyConfigurationFromList = false;
      state.copyConfigurationFromList = [];
    },
    getCopyConfigurationFromListResponse: (state, action) => {
      state.loadingCopyConfigurationFromList = false;
      state.copyConfigurationFromList = (action.payload.asset_list || []).map(
        ({ asset_id, asset_name, asset_ip }: COPY_ASSET_LIST_TYPE) => ({
          label: `${asset_name} (${asset_ip})`,
          value: asset_id,
        }),
      );
    },
    getCopyConfigurationFromListFailed: (state) => {
      state.loadingCopyConfigurationFromList = false;
      state.errorCopyConfigurationFromList = true;
      Snackbar.error("Failed to fetch list. Please try again!");
    },
    getCopyConfigurationFromComponentRequest: (state, action) => {
      state.loadingCopyConfigurationFromComponent = true;
      state.errorCopyConfigurationFromComponent = false;
      state.copyConfigurationFromComponent = {};
    },
    getCopyConfigurationFromComponentResponse: (state, action) => {
      state.loadingCopyConfigurationFromComponent = false;
      state.copyConfigurationFromComponent = action.payload;
    },
    getCopyConfigurationFromComponentFailed: (state) => {
      state.loadingCopyConfigurationFromComponent = false;
      state.errorCopyConfigurationFromComponent = true;
      state.copyConfigurationFromComponent = {};
      Snackbar.error("Failed to copy component. Please try again!");
    },
    clearCopyConfigurationFromComponent: (state) => {
      state.copyConfigurationFromComponent = {};
    },
    resetSelectedFactory: (state) => {
      state.selectedFactory = {};
    },
    resetLoadingState: (state) => {
      state.isAddFactorySuccess = false;
      state.isAddFactoryError = false;
      state.isUpdateFactorySuccess = false;
      state.isupdateFactoryError = false;
      state.isRemoveFactorySuccess = false;
      state.isRemoveFactoryError = false;
      state.isupdateFactoryStatusError = false;
      state.isUpdateFactoryStatusSuccess = false;
    },
    resetFactoryData: (state) => {
      state.factoriesList = [];
      state.factoriesListTotalRowCount = 0;
      state.factoriesListPageNumber = 1;
      state.searchItem = "";
    },
    onSearchForItem: (state, action: SearchtActionTypes) => {
      state.searchItem = action.payload;
    },
    resetAssetComponentsData: (state) => {
      state.assetComponentsData = {};
      state.activeAssetId = "";
    },
  },
});

export const factoryManagementActions = factoryManagementReducer.actions;
export default factoryManagementReducer;
